/**
 * 安全な数値処理ユーティリティ
 * 数値以外の値に対しても安全に処理を行う
 */
/**
 * 安全にtoFixedを実行する関数
 * @param value - 処理する値
 * @param digits - 小数点以下の桁数（デフォルト: 2）
 * @param fallback - 数値でない場合の戻り値（デフォルト: 'N/A'）
 * @returns 整形された文字列
 */
export function safeToFixed(value, digits = 2, fallback = 'N/A') {
    if (value === null || value === undefined) {
        return fallback;
    }
    // 文字列の場合、数値に変換を試みる
    if (typeof value === 'string') {
        const parsed = parseFloat(value);
        if (isNaN(parsed)) {
            return fallback;
        }
        return parsed.toFixed(digits);
    }
    // 数値の場合、そのまま処理
    if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
        return value.toFixed(digits);
    }
    return fallback;
}
/**
 * 安全にtoLocaleStringを実行する関数
 * @param value - 処理する値
 * @param locale - ロケール（デフォルト: 'ja-JP'）
 * @param options - フォーマットオプション
 * @param fallback - 数値でない場合の戻り値（デフォルト: 'N/A'）
 * @returns 整形された文字列
 */
export function safeToLocaleString(value, locale = 'ja-JP', options, fallback = 'N/A') {
    if (value === null || value === undefined) {
        return fallback;
    }
    // 文字列の場合、数値に変換を試みる
    if (typeof value === 'string') {
        const parsed = parseFloat(value);
        if (isNaN(parsed)) {
            return fallback;
        }
        return parsed.toLocaleString(locale, options);
    }
    // 数値の場合、そのまま処理
    if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
        return value.toLocaleString(locale, options);
    }
    return fallback;
}
/**
 * 安全にパーセンテージ形式で表示する関数
 * @param value - 処理する値
 * @param digits - 小数点以下の桁数（デフォルト: 1）
 * @param fallback - 数値でない場合の戻り値（デフォルト: 'N/A'）
 * @returns 整形された文字列（%付き）
 */
export function safeToPercentage(value, digits = 1, fallback = 'N/A') {
    const formattedValue = safeToFixed(value, digits, fallback);
    if (formattedValue === fallback) {
        return fallback;
    }
    return `${formattedValue}%`;
}
/**
 * 安全に数値かどうかを判定する関数
 * @param value - チェックする値
 * @returns 数値として有効かどうか
 */
export function isValidNumber(value) {
    if (value === null || value === undefined) {
        return false;
    }
    if (typeof value === 'number') {
        return !isNaN(value) && isFinite(value);
    }
    if (typeof value === 'string') {
        const parsed = parseFloat(value);
        return !isNaN(parsed) && isFinite(parsed);
    }
    return false;
}
/**
 * 安全に整数として表示する関数
 * @param value - 処理する値
 * @param fallback - 数値でない場合の戻り値（デフォルト: 'N/A'）
 * @returns 整形された文字列
 */
export function safeToInteger(value, fallback = 'N/A') {
    if (!isValidNumber(value)) {
        return fallback;
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return Math.floor(numValue).toString();
}
/**
 * 安全に配列の長さを取得する関数
 * @param array - チェックする配列
 * @param fallback - 配列でない場合の戻り値（デフォルト: 0）
 * @returns 配列の長さまたはフォールバック値
 */
export function safeArrayLength(array, fallback = 0) {
    if (Array.isArray(array)) {
        return array.length;
    }
    return fallback;
}
/**
 * 安全に配列の長さを文字列として取得する関数
 * @param array - チェックする配列
 * @param fallback - 配列でない場合の戻り値（デフォルト: 'N/A'）
 * @returns 配列の長さの文字列またはフォールバック値
 */
export function safeArrayLengthString(array, fallback = 'N/A') {
    if (Array.isArray(array)) {
        return array.length.toString();
    }
    return fallback;
}
/**
 * 安全に配列かどうかを判定する関数
 * @param value - チェックする値
 * @returns 配列かどうか
 */
export function isValidArray(value) {
    return Array.isArray(value);
}
